import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowsClockwise, WarningCircle } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Captcha({ answer, onChange, reloadKey = 0 }) {
  const [image, setImage] = useState('');
  const [status, setStatus] = useState('loading');
  const requestRef = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setStatus('loading');
    try {
      const { data } = await axios.get(`${API}/auth/captcha`, { timeout: 15000 });
      if (requestId !== requestRef.current) return;
      setImage(data.image);
      setStatus('ready');
      onChangeRef.current({ captcha_id: data.captcha_id, captcha_answer: '' });
    } catch (error) {
      if (requestId !== requestRef.current) return;
      setImage('');
      setStatus('error');
      onChangeRef.current({ captcha_id: '', captcha_answer: '' });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  return (
    <div data-testid="captcha-block">
      <Label htmlFor="captcha-answer">Security Check</Label>
      <div className="flex items-center gap-2 mt-1 mb-2">
        <div className="flex-1 h-[70px] bg-black/60 border border-white/10 rounded-md overflow-hidden flex items-center justify-center px-3">
          {status === 'ready' && image ? (
            <img
              src={image}
              alt="Security code"
              className="h-[70px] w-full object-contain select-none"
              draggable="false"
              data-testid="captcha-image"
            />
          ) : status === 'error' ? (
            <button
              type="button"
              onClick={load}
              className="flex items-center gap-2 text-xs text-rose-300 hover:text-rose-200"
              data-testid="captcha-error"
            >
              <WarningCircle size={16} />
              Code did not load. Tap to try again
            </button>
          ) : (
            <span className="text-xs text-muted-foreground animate-pulse" data-testid="captcha-loading">
              Loading security code...
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          aria-label="Refresh security code"
          className="p-3 rounded-full border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors"
          data-testid="captcha-refresh-button"
        >
          <ArrowsClockwise size={20} className={status === 'loading' ? 'animate-spin' : ''} />
        </button>
      </div>
      <Input
        id="captcha-answer"
        type="text"
        autoComplete="off"
        autoCapitalize="characters"
        placeholder="Enter the code above"
        value={answer}
        onChange={(e) => onChange({ captcha_answer: e.target.value })}
        required
        data-testid="captcha-input"
      />
      <p className="text-[11px] text-muted-foreground mt-1">Type the 5 characters shown. Not case-sensitive.</p>
    </div>
  );
}
